import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, HStack, Icon } from '@chakra-ui/react';

const StatCard = ({ value, label, icon, duration = 2000, ...rest }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const startValue = 0;

  const valueStr = String(value);
  const prefix = valueStr.match(/^[^\d]*/)?.[0] || '';
  const suffix = valueStr.match(/[^\d.]*$/)?.[0] || '';
  const cleanValue = valueStr.replace(/[^0-9.]/g, '');
  const endValue = parseFloat(cleanValue) || 0;
  const isDecimal = cleanValue.includes('.');
  const decimalPlaces = isDecimal ? cleanValue.split('.')[1]?.length || 0 : 0;

  const elementId = `stat-${label.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const startAnimation = () => {
      if (countRef.current) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setCount(endValue);
        return;
      }
      const step = (endValue - startValue) / (duration / 16);
      let current = startValue;
      const counter = setInterval(() => {
        current += step;
        if (current >= endValue) {
          clearInterval(counter);
          setCount(endValue);
          countRef.current = null;
          return;
        }
        setCount(isDecimal ? current.toFixed(decimalPlaces) : Math.floor(current));
      }, 16);
      countRef.current = counter;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById(elementId);
    if (element) observer.observe(element);

    return () => {
      if (countRef.current) clearInterval(countRef.current);
      if (element) observer.disconnect();
    };
  }, [endValue, duration, isDecimal, decimalPlaces, elementId]);

  const formattedCount = Number(count) >= 1000
    ? Number(count).toLocaleString(undefined, { maximumFractionDigits: decimalPlaces })
    : count;

  return (
    <HStack
      id={elementId}
      spacing={4}
      align="center"
      minW={0}
      px={{ lg: 5 }}
      borderRight={{ lg: '1px solid rgba(255,255,255,0.06)' }}
      _last={{ borderRight: 'none' }}
      {...rest}
    >
      {icon && (
        <Box flexShrink={0}>
          <Icon as={icon} boxSize={7} color="#D4A85F" strokeWidth={1.7} />
        </Box>
      )}
      <Box minW={0}>
        <Text
          fontFamily="heading"
          fontWeight={500}
          fontSize={{ base: '1.55rem', sm: '1.65rem', md: '2rem', lg: '2.15rem' }}
          lineHeight={1}
          color="#F5F7FA"
          letterSpacing="0"
        >
          {prefix}{formattedCount}{suffix}
        </Text>
        <Text
          fontFamily="body"
          fontSize="xs"
          color="#D6DCE5"
          fontWeight={400}
          mt={1.5}
          letterSpacing="0.01em"
        >
          {label}
        </Text>
      </Box>
    </HStack>
  );
};

export default StatCard;
